import axios from 'axios';
import { Config } from '../../Common/Config';

// ── Analytics client (booking-analytics/* endpoints) ──────────────────────────
const getApiClient = () =>
  axios.create({
    baseURL: `${Config.url}booking-analytics`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
    },
  });

// ── Trace-log client — matches prod URL: /api/admin/v1/trace-logs/page/{page} ─
// Prod pattern (from api.js on prod branch):
//   let url = `${API_BASE_URL}trace-logs/page/${page}`;
// API_BASE_URL = Config.url = https://api.ldcbe.webdezign.uk/api/admin/v1/
const getTraceApiClient = () =>
  axios.create({
    baseURL: Config.url,                        // https://.../api/admin/v1/
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
    },
  });

export const BookingAnalyticsApi = {
  /**
   * GET /booking-analytics/analytics
   * Fetch aggregated dashboard data (charts, funnels, conversion rates).
   * @param {{ date_from: string, date_to: string }} params
   */
  getDashboard: (params) =>
    getApiClient()
      .get('/analytics', { params })
      .then((r) => r.data),

  /**
   * GET /trace-logs/page/{page}?status=&module=&search=&start_date=&end_date=&per_page=
   * Paginated & filtered trace log list — matches prod api.js URL pattern.
   * Response: { success, data: { data: [...], meta: { total, per_page, current_page, last_page }, summary: { total, confirmed, expired, abandoned, failed, pending } } }
   * @param {{ page?: number, per_page?: number, status?: string, module?: string, search?: string, start_date?: string, end_date?: string }} params
   */
  getLogs: ({ page = 1, ...filters } = {}) => {
    const params = new URLSearchParams();
    if (filters.per_page)  params.append('per_page',   filters.per_page);
    if (filters.status && filters.status !== 'all')   params.append('status',     filters.status);
    if (filters.module && filters.module !== 'all')   params.append('module',     filters.module);
    if (filters.search)    params.append('search',     filters.search);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date)   params.append('end_date',   filters.end_date);
    // date_from / date_to aliases (analytics tab uses these names)
    if (filters.date_from)  params.append('start_date', filters.date_from);
    if (filters.date_to)    params.append('end_date',   filters.date_to);

    const qs = params.toString();
    const url = `trace-logs/page/${page}${qs ? `?${qs}` : ''}`;
    return getTraceApiClient()
      .get(url)
      .then((r) => r.data);
  },

  /**
   * GET /trace-logs/{session_id}
   * Single trace log detail (raw JSON steps).
   * @param {string} sessionId
   */
  getLogDetail: (sessionId) =>
    getTraceApiClient()
      .get(`trace-logs/${sessionId}`)
      .then((r) => r.data),

  /**
   * POST /booking-analytics/export
   * Trigger CSV / JSON export as a binary blob download.
   * @param {{ type: string, date_from: string, date_to: string }} data
   */
  export: (data) =>
    getApiClient().post('/export', data, { responseType: 'blob' }),

  /**
   * POST /booking-analytics/sync
   * Trigger a manual background data sync.
   * @param {{ force?: boolean, from?: string, to?: string }} [data]
   */
  sync: (data = {}) =>
    getApiClient().post('/sync', data).then((r) => r.data),

  /**
   * GET /booking-analytics/sync/status
   * Check background sync status.
   */
  getSyncStatus: () =>
    getApiClient().get('/sync/status').then((r) => r.data),
};

// Backwards-compat alias — analyticsApi.js calls BookingAnalyticsApi.triggerSync()
BookingAnalyticsApi.triggerSync = BookingAnalyticsApi.sync;

